import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, EmptyState, SparkIcon, TooltipTrigger } from '@spark';
import { useAuth } from '@/features/auth/AuthContext';
import { TaskCard } from './task/TaskCard.jsx';
import { DeleteProjectModal } from './DeleteProjectModal.jsx';
import { ProjectOverflowMenu } from './ProjectOverflowMenu.jsx';
import { UsecasesBulkActionsBar } from './UsecasesBulkActionsBar.jsx';
import { resolveProjectDeleteScope } from '@/features/tasks/utils/projectDeleteScope';
import { getMoveDestinations, taskSelectionKey } from '@/features/tasks/utils/bulkMoveTasks';
import { selectKeyRange } from '@/features/tasks/utils/cardSelectionClick';
import { useMarqueeSelection } from '@/features/tasks/hooks/useMarqueeSelection';
import { useCardContextMenu } from '@/features/tasks/hooks/useCardContextMenu';
import { getTaskTitle } from './task/taskSummary';
import './GroupedFlatTableView.css';
import './NewProjectTaskButton.css';

/**
 * Drilled-in project view (cards view): back chevron + "<project> (<count>)"
 * header with the project actions. When tasks are selected, bulk actions
 * sit next to the title (the overflow menu is hidden). Figma: Model Creation `3021:4308` /
 * `3480:16956`. Bulk marquee: Projects `8001:3358`.
 */
export const ProjectTasksView = ({
    app,
    projectName,
    allApps,
    isLoading = false,
    onBack,
    onDeleteUsecase,
    onBulkDeleteUsecases,
    onBulkMoveUsecases,
    onDeleteProject,
    onEditProject,
    onModelDeployment,
    onNewTask,
    onDuplicateUsecase,
    onEditTask,
}) => {
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [selectedKeys, setSelectedKeys] = useState([]);
    const selectedKeysRef = useRef([]);
    const snapshotKeysRef = useRef(null);
    const anchorKeyRef = useRef(null);
    const gridRef = useRef(null);
    selectedKeysRef.current = selectedKeys;
    const { role } = useAuth();
    const isViewer = role === 'Viewer';

    const name = app?.group_name || projectName;
    const subGroups = app?.sub_groups || [];
    const deleteScope = resolveProjectDeleteScope(app, allApps);
    const isFilteredDelete = deleteScope.mode === 'filtered-tasks';

    const allTaskKeys = useMemo(
        () => subGroups.map((subGroup) => taskSelectionKey(subGroup, name)),
        [subGroups, name]
    );

    useEffect(() => {
        setSelectedKeys((prev) => prev.filter((key) => allTaskKeys.includes(key)));
    }, [allTaskKeys]);

    const selectedSubGroups = useMemo(
        () => subGroups.filter((subGroup) => selectedKeys.includes(taskSelectionKey(subGroup, name))),
        [subGroups, selectedKeys, name]
    );

    const selectedUsecases = useMemo(
        () => selectedSubGroups.flatMap((subGroup) => subGroup.usecases || []),
        [selectedSubGroups]
    );

    const destinations = useMemo(
        () => getMoveDestinations(allApps, [name]),
        [allApps, name]
    );

    const applyMarqueeIds = useCallback((ids, { additive = false, preview = false } = {}) => {
        if (preview) {
            if (snapshotKeysRef.current == null) {
                snapshotKeysRef.current = additive ? selectedKeysRef.current : [];
            }
            setSelectedKeys(
                additive ? [...new Set([...snapshotKeysRef.current, ...ids])] : ids
            );
            return;
        }
        const base = additive ? (snapshotKeysRef.current || selectedKeysRef.current) : [];
        const next = additive ? [...new Set([...base, ...ids])] : ids;
        setSelectedKeys(next);
        snapshotKeysRef.current = null;
        if (ids.length > 0) {
            anchorKeyRef.current = ids[ids.length - 1];
        }
    }, []);

    const { marquee, isDragging, wasDrag } = useMarqueeSelection({
        containerRef: gridRef,
        enabled: !isViewer && !!app,
        onSelect: applyMarqueeIds,
    });

    const handleSelectAll = useCallback(() => {
        setSelectedKeys(allTaskKeys);
    }, [allTaskKeys]);

    const handleDeselectAll = useCallback(() => {
        setSelectedKeys([]);
    }, []);

    const handleToggleSelect = useCallback((key) => {
        anchorKeyRef.current = key;
        setSelectedKeys((prev) => (
            prev.includes(key) ? prev.filter((item) => item !== key) : [...prev, key]
        ));
    }, []);

    const handleRangeSelect = useCallback((key) => {
        setSelectedKeys(selectKeyRange(allTaskKeys, anchorKeyRef.current, key));
    }, [allTaskKeys]);

    const { contextMenu, closeContextMenu, onCardContextMenu } = useCardContextMenu({
        selectedCount: selectedKeys.length,
        setSelectedKeys,
        setAnchorKey: (key) => { anchorKeyRef.current = key; },
    });

    const handleBulkDelete = useCallback(async () => {
        if (selectedUsecases.length === 0) return;
        const res = await onBulkDeleteUsecases?.(selectedUsecases);
        if (res?.success) setSelectedKeys([]);
    }, [onBulkDeleteUsecases, selectedUsecases]);

    const handleBulkMove = useCallback(async (destName) => {
        const movedAllVisible = selectedKeys.length > 0 && selectedKeys.length === allTaskKeys.length;
        const res = await onBulkMoveUsecases?.(selectedUsecases, destName);
        if (res?.success) {
            setSelectedKeys([]);
            if (movedAllVisible) onBack?.();
        }
        return res;
    }, [allTaskKeys.length, onBack, onBulkMoveUsecases, selectedKeys.length, selectedUsecases]);

    // Single-card "Move Task" (card ⋯ menu). Like handleBulkMove, leave the
    // project view when the moved task was the last one shown.
    const handleCardMove = useCallback(async (usecasesToMove, destName) => {
        const res = await onBulkMoveUsecases?.(usecasesToMove, destName);
        if (res?.success && subGroups.length === 1) onBack?.();
        return res;
    }, [onBack, onBulkMoveUsecases, subGroups.length]);

    const handleDeleteConfirm = async () => {
        if (!onDeleteProject || !app) return;
        setDeleteLoading(true);
        try {
            const result = await onDeleteProject(app);
            setShowDeleteModal(false);
            if (result?.projectDeleted) {
                onBack?.();
            }
        } catch (error) {
            console.error('Error deleting project:', error);
        } finally {
            setDeleteLoading(false);
        }
    };

    const newTaskButton = (
        <Button
            className="fs-unmask new-project-task-btn"
            variant="text"
            size="medium"
            disabled={isViewer}
            disableWhenReadOnly
            onClick={() => onNewTask?.(name)}
        >
            Add new task
        </Button>
    );

    const selectionActive = !isViewer && selectedKeys.length > 0;

    return (
        <div id={`project-${name}`} className="home-page-cards-view">
            {/* Header: back chevron + project title, actions on the right */}
            <div className="home-page-cards-heading">
                <div className="home-page-cards-heading__lead">
                    <div role="presentation" onClick={() => onBack?.()}>
                        <Button
                            variant="ghost"
                            size="small"
                            type="button"
                            aria-label="Back to all projects"
                            icon={<SparkIcon slug="chevron-left-large" size="medium" />}
                            onClick={() => onBack?.()}
                        />
                    </div>
                    <div className="fs-unmask home-page-cards-heading__title">
                        {`${name} (${subGroups.length})`}
                    </div>
                    {selectionActive ? (
                        <UsecasesBulkActionsBar
                            variant="inline"
                            selectedCount={selectedKeys.length}
                            totalCount={allTaskKeys.length}
                            onSelectAll={handleSelectAll}
                            onDeselectAll={handleDeselectAll}
                            onDelete={handleBulkDelete}
                            onMove={onBulkMoveUsecases ? handleBulkMove : undefined}
                            destinations={destinations}
                            itemNames={selectedSubGroups.map((subGroup) => getTaskTitle(subGroup))}
                            contextMenu={contextMenu}
                            onContextMenuClose={closeContextMenu}
                        />
                    ) : role !== 'Viewer' && app ? (
                        <ProjectOverflowMenu
                            align="start"
                            deleteLabel={isFilteredDelete ? 'Delete filtered tasks' : 'Delete Project'}
                            onEdit={() => onEditProject && onEditProject(app)}
                            onDelete={() => setShowDeleteModal(true)}
                        />
                    ) : null}
                </div>
                <div className="home-page-cards-heading__actions">
                    {isViewer ? (
                        <TooltipTrigger text="Viewers cannot create new project tasks">
                            {newTaskButton}
                        </TooltipTrigger>
                    ) : (
                        newTaskButton
                    )}
                </div>
            </div>

            {app ? (
                <div
                    ref={gridRef}
                    className={isDragging ? 'home-page-marquee-surface is-dragging' : 'home-page-marquee-surface'}
                >
                    <div className="home-page-cards-grid">
                    {subGroups.map((subGroup) => {
                        const key = taskSelectionKey(subGroup, name);
                        return (
                            <div
                                key={`task-${key}`}
                                className="home-page-card-cell"
                                data-task-key={key}
                                onContextMenu={isViewer ? undefined : (event) => onCardContextMenu(
                                    event,
                                    key,
                                    () => handleToggleSelect(key),
                                )}
                            >
                                <TaskCard
                                    subGroup={subGroup}
                                    groupName={name}
                                    selected={selectedKeys.includes(key)}
                                    selectionKey={key}
                                    onToggleSelect={isViewer ? undefined : () => handleToggleSelect(key)}
                                    onRangeSelect={isViewer ? undefined : () => handleRangeSelect(key)}
                                    shouldSuppressClick={wasDrag}
                                    onDeleteUsecase={onDeleteUsecase}
                                    onBulkDeleteUsecases={onBulkDeleteUsecases}
                                    onModelDeployment={onModelDeployment}
                                    onDuplicateUsecase={onDuplicateUsecase}
                                    onEditTask={onEditTask}
                                    onMoveUsecases={onBulkMoveUsecases ? handleCardMove : undefined}
                                    moveDestinations={destinations}
                                />
                            </div>
                        );
                    })}
                    </div>
                    {marquee ? (
                        <div
                            className="home-page-marquee"
                            style={{
                                left: marquee.left,
                                top: marquee.top,
                                width: marquee.width,
                                height: marquee.height,
                            }}
                        />
                    ) : null}
                </div>
            ) : isLoading ? null : (
                // Project excluded by the active filters/search (or deleted).
                <div className="home-page-empty-state">
                    <div className="home-page-no-results-card">
                        <EmptyState
                            title="We couldn't find any matches"
                            subtitle="Try a different search."
                            showMainButton={false}
                        />
                    </div>
                </div>
            )}

            <DeleteProjectModal
                visible={showDeleteModal}
                onCancel={() => setShowDeleteModal(false)}
                onConfirm={handleDeleteConfirm}
                projectName={name}
                loading={deleteLoading}
                isFilteredDelete={isFilteredDelete}
                filteredTaskCount={deleteScope.visibleTaskCount}
            />
        </div>
    );
};

export default ProjectTasksView;
